import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useMyRequests } from '../hooks/useApprovalCenter';
import type { ApprovalStatus, ApprovalSearchCondition, ApiError } from '../types/approvalCenter';
import type { StatusTone } from '@ui-kit';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_FILTERS: ApprovalSearchCondition = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: 'submittedAt,desc',
};

function statusTone(status: ApprovalStatus): StatusTone {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED') return 'warning';
  return 'info';
}

function formatDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function SkeletonRow() {
  return (
    <tr>
      {[45, 65, 30, 20, 25].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * MyApprovalRequestPage — 내가 올린 결재 (상태별 필터)
 * 라우트: /approval-center/mine
 * 권한: AUTHENTICATED
 */
export function MyApprovalRequestPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ApprovalSearchCondition>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);

  const requestsQuery = useMyRequests(filters);
  const pageData = requestsQuery.data;

  const handleStatusFilter = useCallback((status: ApprovalStatus | '') => {
    setPage(0);
    setFilters({ ...DEFAULT_FILTERS, status: status || undefined });
  }, []);

  const handlePageChange = useCallback((p: number) => {
    setPage(p);
    setFilters((prev) => ({ ...prev, page: p }));
  }, []);

  const columns = [
    t('approval.inbox.column.form'),
    t('approval.inbox.column.title'),
    t('approval.inbox.column.status'),
    t('approval.inbox.column.currentStep'),
    t('approval.inbox.column.submittedAt'),
  ];

  const isLoading = requestsQuery.isLoading;
  const isError = requestsQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? true);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('approval.inbox.tab.mine')}
        breadcrumb={
          <span>
            <button
              type="button"
              onClick={() => navigate('/approval-center')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                padding: 0,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              ← {t('approval.inbox.title')}
            </button>
            {' / '}
            {t('approval.inbox.tab.mine')}
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/approval-center/new')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('approval.inbox.button.new')}
          </button>
        }
      />

      {/* 상태 필터 탭 */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
          flexWrap: 'wrap',
        }}
      >
        {[
          { value: '' as const, label: '전체' },
          { value: 'PENDING' as const, label: t('approval.status.PENDING') },
          { value: 'APPROVED' as const, label: t('approval.status.APPROVED') },
          { value: 'REJECTED' as const, label: t('approval.status.REJECTED') },
        ].map(({ value, label }) => {
          const isActive = (filters.status ?? '') === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => handleStatusFilter(value)}
              aria-pressed={isActive}
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                background: isActive ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                color: isActive ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                border: `1px solid ${isActive ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* 총 건수 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-3)',
          }}
        >
          총 {pageData.totalElements.toLocaleString()}건
        </p>
      )}

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* Loading */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('approval.inbox.tab.mine')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {columns.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        )}

        {/* 오류 */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('approval.error.network')}
              message={(requestsQuery.error as unknown as ApiError)?.message ?? t('approval.error.network')}
              onRetry={() => void requestsQuery.refetch()}
            />
          </div>
        )}

        {/* 빈 상태 */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('approval.inbox.empty.mine')}
              description={t('approval.inbox.empty.mine')}
              action={
                <button
                  type="button"
                  onClick={() => navigate('/approval-center/new')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('approval.inbox.button.new')}
                </button>
              }
            />
          </div>
        )}

        {/* 기본 — DataTable */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('approval.inbox.tab.mine')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('approval.inbox.tab.mine')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {columns.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={row.title}
                      onClick={() => navigate(`/approval-center/${row.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/approval-center/${row.id}`);
                        }
                      }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                          {row.formLabel}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {row.title}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <StatusPill tone={statusTone(row.status)}>
                          {t(`approval.status.${row.status}`, { defaultValue: row.statusLabel })}
                        </StatusPill>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                          {row.currentStepLabel ?? (row.currentStep
                            ? t(`approval.step.${row.currentStep}`, { defaultValue: row.currentStep })
                            : '-')}
                        </span>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
                          {formatDate(row.submittedAt, i18n.language)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {pageData.totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  background: 'var(--color-surface-muted)',
                  borderTop: '1px solid var(--color-surface-border)',
                  flexWrap: 'wrap',
                  gap: 'var(--spacing-2)',
                }}
              >
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                  {pageData.number * pageData.size + 1}–{Math.min((pageData.number + 1) * pageData.size, pageData.totalElements)} / {pageData.totalElements.toLocaleString()}건
                </span>
                <nav aria-label="페이지 탐색" style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                  <button
                    type="button"
                    disabled={pageData.first}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="이전 페이지"
                    style={{ minWidth: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', cursor: pageData.first ? 'not-allowed' : 'pointer', opacity: pageData.first ? 0.5 : 1 }}
                  >
                    &lt;
                  </button>
                  {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handlePageChange(i)}
                      aria-label={`${i + 1}페이지`}
                      aria-current={i === pageData.number ? 'page' : undefined}
                      style={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: 'var(--radius-sm)',
                        background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                        border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                        color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-xs)',
                        cursor: 'pointer',
                        fontWeight: i === pageData.number ? 600 : 400,
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={pageData.last}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="다음 페이지"
                    style={{ minWidth: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', cursor: pageData.last ? 'not-allowed' : 'pointer', opacity: pageData.last ? 0.5 : 1 }}
                  >
                    &gt;
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 520px; }
        }
      `}</style>
    </div>
  );
}
